import { useState, useMemo, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { usePersistedFilters } from "../hooks/usePersistedFilters";
import { useScrollEdges } from "../hooks/useScrollEdges";
import { useDragScroll } from "../hooks/useDragScroll";
import { TH_CLASS, TD_CLASS } from "../lib/tableStyles";
import { Route } from "../routes/helpdesk/knowledge-base/index";
import {
  useArticles,
  useArticleStats,
  useMostViewedArticles,
  useRecentlyViewedArticles,
} from "../hooks/useArticles";
import { queryKeys } from "../lib/queryKeys";
import { useAdminOrganizations } from "../hooks/useAdminOrganizations";
import { TreeMultiSelectFilter } from "../components/tree-select/TreeMultiSelectFilter";
import { collectionToOption } from "../components/tree-select/treeOptions";
import { apiService } from "../services/api";
import { useArticleStatuses } from "../hooks/useLookups";
import { showToast } from "../lib/toast";
import { getApiErrorMessage } from "../lib/apiErrors";
// Removed useAssignableUsers - no longer needed for bulk actions
import { useDateFormat } from "../hooks/useDateFormat";
import type { ArticleDto, ArticleFilterRequest } from "../types/articles";
import {
  ArticleStatus,
  ArticleStatusLabels,
  ArticleStatusColors,
  ArticleVisibility,
  ArticleVisibilityLabels,
  ArticleVisibilityColors,
} from "../types/articles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/ui/date-picker";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArticleListSkeleton } from "../components/LoadingSkeleton";
import { Checkbox } from "../components/ui/checkbox";
import { ArticleBulkActionsBar } from "../components/knowledge-base/ArticleBulkActionsBar";
import { NeedsAttentionCard } from "../components/knowledge-base/NeedsAttentionCard";
import { SearchInsightsCard } from "../components/knowledge-base/SearchInsightsCard";
import {
  Plus,
  Library,
  Eye,
  User,
  BookOpen,
  TrendingUp,
  FileText,
  Star,
  X,
  Building2,
} from "lucide-react";
import { MultiSelectFilter } from "../components/MultiSelectFilter";
import { CollapsibleFilterBar } from "../components/CollapsibleFilterBar";

export function ArticleList() {
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { formatDateTime, formatDateOnly, parseDateToUTC } = useDateFormat();
  const { availableOrganizations } = useAuth();
  const searchParams = Route.useSearch();

  // Define default filter values for localStorage persistence
  const defaultFilterValues = {
    sortBy: 'updatedAt',
    sortDescending: true,
    page: 1,
    pageSize: 20,
  };

  // Initialize filter persistence (global across all orgs)
  const storageKey = `helpdesk.knowledgeBase.filters`;
  const { updateFilters, clearFilters: clearPersistedFilters, isInitialized } = usePersistedFilters(
    storageKey,
    defaultFilterValues,
    searchParams
  );

  // Filter state - use URL params
  const [searchInput, setSearchInput] = useState(searchParams.search || "");

  // Sync search input with URL params when they change
  useEffect(() => {
    setSearchInput(searchParams.search || "");
  }, [searchParams.search]);

  // Get current filter values from URL
  const searchQuery = searchParams.search || "";
  const statusFilters: ArticleStatus[] = useMemo(() => (searchParams.statuses ?? []) as ArticleStatus[], [searchParams.statuses]);
  const visibilityFilters: ArticleVisibility[] = useMemo(() => (searchParams.visibilities ?? []) as ArticleVisibility[], [searchParams.visibilities]);
  const collectionFilters: string[] = useMemo(() => searchParams.collections ?? [], [searchParams.collections]);
  const organizationFilters: string[] = useMemo(() => searchParams.organizationIds ?? [], [searchParams.organizationIds]);
  const featuredFilter = searchParams.featured !== undefined ? searchParams.featured : "";
  const fromDate = searchParams.fromDate || "";
  const toDate = searchParams.toDate || "";
  const sortBy = searchParams.sortBy || "updatedAt";
  const sortDescending = searchParams.sortDescending ?? true;
  const currentPage = searchParams.page || 1;
  const pageSize = searchParams.pageSize || 20;

  // Articles ticked for a bulk action - on the page being shown, so a new page, filter or sort starts afresh
  const [selected, setSelected] = useState<Map<string, ArticleDto>>(new Map());

  // Build filter object for query
  const filter = useMemo(
    (): ArticleFilterRequest => ({
      page: currentPage,
      pageSize,
      sortBy,
      sortDescending,
      ...(searchQuery && { searchQuery }),
      ...(statusFilters.length > 0 && { statuses: statusFilters }),
      ...(visibilityFilters.length > 0 && { visibilities: visibilityFilters }),
      ...(collectionFilters.length > 0 && { collectionIds: collectionFilters }),
      ...(organizationFilters.length > 0 && { organizationIds: organizationFilters }),
      ...(featuredFilter !== "" && { isFeatured: featuredFilter }),
      ...(fromDate && { createdAfter: parseDateToUTC(fromDate, true) || undefined }),  // Start of day
      ...(toDate && { createdBefore: parseDateToUTC(toDate, false) || undefined }),   // End of day
    }),
    [
      currentPage,
      pageSize,
      sortBy,
      sortDescending,
      searchQuery,
      statusFilters,
      visibilityFilters,
      collectionFilters,
      organizationFilters,
      featuredFilter,
      fromDate,
      toDate,
      parseDateToUTC,
    ]
  );

  // Keyed on the filter's value: the object itself is rebuilt on every render (parseDateToUTC is a new function each time)
  const filterKey = JSON.stringify(filter);
  useEffect(() => setSelected(new Map()), [filterKey]);

  // Calculate active filter count for the collapsible filter bar
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (statusFilters.length > 0) count++;
    if (visibilityFilters.length > 0) count++;
    if (collectionFilters.length > 0) count++;
    // Only count organization filter if user has multiple orgs AND not all are selected
    if (availableOrganizations && availableOrganizations.length > 1 &&
        organizationFilters.length > 0 && organizationFilters.length < availableOrganizations.length) {
      count++;
    }
    if (featuredFilter !== "") count++;
    if (fromDate) count++;
    if (toDate) count++;
    return count;
  }, [
    searchQuery,
    statusFilters,
    visibilityFilters,
    collectionFilters,
    organizationFilters,
    featuredFilter,
    fromDate,
    toDate,
    availableOrganizations,
  ]);

  // Use TanStack Query hooks
  const { data, isLoading, isFetching, error, isError } = useArticles(filter);
  const { data: stats, isFetching: isStatsFetching } = useArticleStats(organizationFilters.length > 0 ? organizationFilters : undefined);
  const { data: articleStatuses = [] } = useArticleStatuses();
  // Removed users query - no longer needed for bulk actions

  // Determine which organization IDs to fetch collections for
  // Use selected org filters, or all available organizations if none selected
  const selectedOrgIds = useMemo(() => {
    return organizationFilters.length > 0
      ? organizationFilters
      : (availableOrganizations?.map(org => org.id) || []);
  }, [organizationFilters, availableOrganizations]);

  // Fetch knowledge base collections for all selected organizations in parallel
  const collectionQueries = useQueries({
    queries: selectedOrgIds.map(orgId => ({
      queryKey: queryKeys.articleCollections.list(orgId),
      queryFn: async () => {
        const response = await apiService.getArticleCollections(orgId);
        if (!response.success) {
          throw new Error(getApiErrorMessage(response, 'Failed to fetch collections'));
        }
        return Array.isArray(response.data) ? response.data : [];
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Removed bulk operation mutations

  // Popular articles - filter by organization when organization filter is applied
  const { data: mostViewedArticles = [], isFetching: isMostViewedFetching } = useMostViewedArticles(
    5,
    undefined,
    organizationFilters.length > 0 ? organizationFilters : undefined
  );
  const { data: recentlyViewedArticles = [], isFetching: isRecentlyViewedFetching } = useRecentlyViewedArticles(
    5,
    organizationFilters.length > 0 ? organizationFilters : undefined
  );

  // Admins manage the knowledge base's collections in Org Settings; offer the way there for one organization at a time
  const { data: adminOrganizations = [] } = useAdminOrganizations();
  const manageCollectionsOrgId =
    selectedOrgIds.length === 1 && adminOrganizations.some((o) => o.id === selectedOrgIds[0])
      ? selectedOrgIds[0]
      : adminOrganizations.length === 1 ? adminOrganizations[0].id : null;

  // Combine and dedupe collections from all organizations
  const safeCollections = useMemo(() => {
    const allCollections = collectionQueries
      .filter(q => q.isSuccess && q.data)
      .flatMap(q => q.data || []);
    // Dedupe by ID - keep first occurrence
    return [...new Map(allCollections.map(c => [c.id, c])).values()];
  }, [collectionQueries]);

  // Wait for every per-org collection query to settle before running cleanup — using
  // `.some()` would let the cleanup branch fire as soon as the first org returns, with
  // `safeCollections` missing data from slower orgs, and incorrectly prune valid IDs.
  const collectionsLoaded =
    collectionQueries.length === 0 ||
    collectionQueries.every(q => q.isSuccess || q.isError);

  // Create stable key for available options to detect changes
  const availableCollectionIdsKey = useMemo(() =>
    safeCollections.map(c => c.id).sort().join(','),
    [safeCollections]
  );

  // Auto-deselect invalid collection filters when available options change
  // Also clear collection filters when multiple orgs are selected (collection dropdown is hidden)
  useEffect(() => {
    // Get current filter values from URL params
    const currentCollectionFilters = searchParams.collections ?? [];

    // Clear collection filters when multiple orgs are selected (dropdown hidden)
    if (selectedOrgIds.length !== 1 && currentCollectionFilters.length > 0) {
      updateFilters({ collections: undefined });
      showToast.info(
        "Filters Updated",
        `${currentCollectionFilters.length} collection filter${currentCollectionFilters.length > 1 ? 's' : ''} removed - collection filter requires single organization selection`
      );
      return;
    }

    // Check for invalid collections when single org is selected
    if (selectedOrgIds.length === 1 && collectionsLoaded && safeCollections.length > 0) {
      const validCollectionIds = new Set(safeCollections.map(c => c.id));
      const invalidCollections = currentCollectionFilters.filter(id => !validCollectionIds.has(id));

      if (invalidCollections.length > 0) {
        const validCollections = currentCollectionFilters.filter(id => validCollectionIds.has(id));
        updateFilters({ collections: validCollections.length > 0 ? validCollections : undefined });
        showToast.info(
          "Filters Updated",
          `${invalidCollections.length} collection filter${invalidCollections.length > 1 ? 's' : ''} removed - not available in selected organization`
        );
      }
    }
  }, [availableCollectionIdsKey, collectionsLoaded, updateFilters, searchParams.collections, selectedOrgIds.length, safeCollections]);

  // Auto-select all organizations if none selected (only after filter restoration is complete)
  useEffect(() => {
    if (isInitialized && availableOrganizations && availableOrganizations.length > 0 && organizationFilters.length === 0) {
      updateFilters({ organizationIds: availableOrganizations.map(org => org.id) });
    }
  }, [isInitialized, availableOrganizations, organizationFilters.length, updateFilters]);

  const safeArticleStatuses = Array.isArray(articleStatuses) ? articleStatuses : [];
  // Removed safeUsers - no longer needed for bulk actions

  const articles = data?.articles || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  const allOnPageSelected = articles.length > 0 && articles.every((a) => selected.has(a.id));
  const toggleSelected = (article: ArticleDto, checked: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (checked) next.set(article.id, article);
      else next.delete(article.id);
      return next;
    });
  };
  const toggleAllOnPage = (checked: boolean) => {
    setSelected(checked ? new Map(articles.map((a) => [a.id, a])) : new Map());
  };

  const {
    ref: tableScrollRef,
    edges: scrollEdges,
    onScroll: updateScrollEdges,
  } = useScrollEdges<HTMLDivElement>([articles.length, isLoading]);

  const tableDragProps = useDragScroll(tableScrollRef);

  // Helper functions to get status labels and colors from fetched data
  const getStatusLabel = (statusValue: number): string => {
    const status = safeArticleStatuses.find(s => s.value === statusValue);
    return status?.label || ArticleStatusLabels[statusValue as ArticleStatus] || 'Unknown';
  };

  const getStatusColor = (statusValue: number): string => {
    // Keep using hardcoded colors for now, but get label from API
    return ArticleStatusColors[statusValue as ArticleStatus] || 'bg-gray-100 text-gray-800';
  };

  const getOrganizationName = (organizationId: string): string => {
    const org = availableOrganizations?.find(o => o.id === organizationId);
    return org?.name || '—';
  };

  const handleSearch = () => {
    updateFilters({ search: searchInput || undefined, page: 1 });
  };

  const handleSort = (field: string) => {
    const newSortDescending = sortBy === field ? !sortDescending : true;
    updateFilters({ sortBy: field, sortDescending: newSortDescending });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    updateFilters({ pageSize: newPageSize, page: 1 });
  };

  const clearFilters = () => {
    setSearchInput("");
    clearPersistedFilters();
  };

  // Removed all bulk operation functions

  // Show skeleton while filters are being restored
  if (!isInitialized) {
    return <ArticleListSkeleton />;
  }

  // Show full skeleton only when no data exists and loading
  if (isLoading && !data) {
    return <ArticleListSkeleton />;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Knowledge Base</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Manage articles and documentation
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {manageCollectionsOrgId && (
            <Link
              to="/helpdesk/org-settings/$orgId"
              params={{ orgId: manageCollectionsOrgId }}
              search={{ tab: "collections" }}
              className="w-full sm:w-auto"
            >
              <Button variant="outline" className="w-full sm:w-auto">
                <Library className="mr-2 h-4 w-4" />
                Manage Collections
              </Button>
            </Link>
          )}
          <Link to={getPath.newArticle()} className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Create Article
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {(stats || isStatsFetching) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Articles
              </CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isStatsFetching ? (
                  <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                ) : (
                  stats?.totalArticles ?? 0
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats?.archivedArticles
                  ? `Not counting ${stats.archivedArticles.toLocaleString()} archived`
                  : "Published and drafts"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Published</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isStatsFetching ? (
                  <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                ) : (
                  stats?.publishedArticles ?? 0
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Articles available to customers
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isStatsFetching ? (
                  <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                ) : (
                  (stats?.totalViews ?? 0).toLocaleString()
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                All-time article views
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Views This Month
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isStatsFetching ? (
                  <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                ) : (
                  (stats?.viewsThisMonth ?? 0).toLocaleString()
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Views in current month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Drafts</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isStatsFetching ? (
                  <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                ) : (
                  stats?.draftArticles ?? 0
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Articles in draft status
              </p>
            </CardContent>
          </Card>

        </div>
      )}

      <NeedsAttentionCard
        organizationIds={organizationFilters.length > 0 ? organizationFilters : undefined}
        getOrganizationName={getOrganizationName}
      />

      <SearchInsightsCard organizationIds={organizationFilters.length > 0 ? organizationFilters : undefined} />

      {/* Popular Articles Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Most Viewed Articles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Most Viewed Articles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {isMostViewedFetching ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={`skeleton-${index}`} className="p-3 animate-pulse">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-muted rounded-full"></div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))
              ) : mostViewedArticles.length > 0 ? (
                mostViewedArticles.map((article, index: number) => (
                  <Link
                    key={article.id}
                    to={getPath.article(article.id)}
                    className="block p-3 hover:bg-muted/50 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">
                          {article.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Eye className="h-3 w-3" />
                          <span>
                            {article.viewCount.toLocaleString()} views
                          </span>
                          <span>•</span>
                          <span>{formatDateTime(article.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <TrendingUp className="h-8 w-8 text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">No viewed articles yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recently Viewed Articles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Recently Viewed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {isRecentlyViewedFetching ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={`skeleton-${index}`} className="p-3 animate-pulse">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-4 h-4 bg-muted rounded"></div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))
              ) : recentlyViewedArticles.length > 0 ? (
                recentlyViewedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={getPath.article(article.id)}
                    className="block p-3 hover:bg-muted/50 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">
                          {article.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Badge
                            className={`text-xs ${getStatusColor(article.status)}`}
                          >
                            {getStatusLabel(article.status)}
                          </Badge>
                          {article.visibility === ArticleVisibility.Internal && (
                            <Badge className={`text-xs ${ArticleVisibilityColors[ArticleVisibility.Internal]}`}>
                              Internal
                            </Badge>
                          )}
                          <span>•</span>
                          {/* When it was last read, which is what this list is about */}
                          <span>Viewed {formatDateTime(article.lastViewedAt ?? article.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Eye className="h-8 w-8 text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">No recently viewed articles</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Display */}
      {isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <X className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-red-800">
                  Failed to Load Articles
                </h4>
                <p className="text-sm text-red-600 mt-1">
                  {error?.message ||
                    "An unexpected error occurred. Please try again."}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
                className="text-red-600 border-red-200 hover:bg-red-100"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Removed bulk operation error handling */}

      {/* Filters */}
      <CollapsibleFilterBar
        storageKey="helpdesk.knowledgeBase.filtersCollapsed"
        activeFilterCount={activeFilterCount}
        searchSection={
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Search
            </label>
            <div className="flex">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="rounded-r-none pr-8"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      updateFilters({ search: undefined, page: 1 });
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                onClick={handleSearch}
                className="rounded-l-none px-3 sm:px-4"
                disabled={isLoading}
              >
                <span className="hidden sm:inline">Search</span>
                <span className="sm:hidden">Go</span>
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3 sm:space-y-4">
          {/* First row: Primary filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Organization filter - show first since it affects collection availability */}
            {availableOrganizations && availableOrganizations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Organization
                </label>
                <MultiSelectFilter
                  label="Organization"
                  options={availableOrganizations.map(org => ({ value: org.id, label: org.name }))}
                  selectedValues={organizationFilters}
                  onChange={(values) => {
                    updateFilters({ organizationIds: values, page: 1 });
                  }}
                  requireAtLeastOne={true}
                  showSelectAllControls={true}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Status
              </label>
              <MultiSelectFilter
                label="Status"
                options={safeArticleStatuses.map(s => ({ value: s.value.toString(), label: s.label }))}
                selectedValues={statusFilters.map(s => s.toString())}
                onChange={(values) => {
                  updateFilters({ statuses: values.length > 0 ? values.map(v => parseInt(v)) : undefined, page: 1 });
                }}
                showSelectAllControls={true}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Visibility
              </label>
              <MultiSelectFilter
                label="Visibility"
                options={[
                  { value: ArticleVisibility.Public.toString(), label: 'Public' },
                  { value: ArticleVisibility.Internal.toString(), label: 'Internal' },
                ]}
                selectedValues={visibilityFilters.map(v => v.toString())}
                onChange={(values) => {
                  updateFilters({ visibilities: values.length > 0 ? values.map(v => parseInt(v)) : undefined, page: 1 });
                }}
                showSelectAllControls={true}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Featured
              </label>
              <select
                value={featuredFilter !== "" ? featuredFilter.toString() : ""}
                onChange={(e) => {
                  const newValue = e.target.value === "" ? undefined : e.target.value === "true";
                  updateFilters({ featured: newValue, page: 1 });
                }}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring h-[42px]"
              >
                <option value="">All Articles</option>
                <option value="true">Featured Only</option>
                <option value="false">Not Featured</option>
              </select>
            </div>
          </div>

          {/* Second row: Secondary filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Collection filter - only show when exactly one organization is selected */}
            {selectedOrgIds.length === 1 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Collection
                </label>
                <TreeMultiSelectFilter
                  label="Collection"
                  options={safeCollections.map(collectionToOption)}
                  selectedIds={collectionFilters}
                  footnote="A collection includes its sections"
                  onChange={(values) => {
                    updateFilters({ collections: values.length > 0 ? values : undefined, page: 1 });
                  }}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Created From
              </label>
              <DatePicker
                value={fromDate}
                onChange={(newValue) => {
                  updateFilters({ fromDate: newValue, page: 1 });
                }}
                placeholder="Select date"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Created To
              </label>
              <DatePicker
                value={toDate}
                onChange={(newValue) => {
                  updateFilters({ toDate: newValue, page: 1 });
                }}
                placeholder="Select date"
              />
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full h-[42px]"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </CollapsibleFilterBar>

      {selected.size > 0 && (
        <ArticleBulkActionsBar
          // The page's current copies, so a status changed since the tick is what the buttons count
          selected={articles.filter((a) => selected.has(a.id))}
          adminOrganizationIds={adminOrganizations.map((o) => o.id)}
          onDone={() => setSelected(new Map())}
          onClear={() => setSelected(new Map())}
        />
      )}

      {/* Articles Table */}
      <Card>
        <div className="relative">
          {/* Bounded height keeps the horizontal scrollbar in view instead of parking it below
              the last row, and gives the sticky header a scrollport to resolve against. */}
          <div
            ref={tableScrollRef}
            onScroll={updateScrollEdges}
            {...tableDragProps}
            className="max-h-[calc(100vh-22rem)] overflow-auto"
          >
            {/* Fixed layout: Title absorbs the slack, every other column is pinned, so a long
                collection or organisation name truncates instead of widening its column. */}
            <table className="w-full table-fixed min-w-[560px] md:min-w-[800px] lg:min-w-[1250px]">
              <thead className="sticky top-0 z-20 bg-muted">
                <tr>
                  <th className={`${TH_CLASS} w-[44px] cursor-default`} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      // Checked only when the whole page is: the shared checkbox draws a tick for "indeterminate" too,
                      // which would read as everything selected
                      checked={allOnPageSelected}
                      onCheckedChange={(checked) => toggleAllOnPage(checked === true)}
                      disabled={articles.length === 0}
                      aria-label="Select all articles on this page"
                    />
                  </th>
                  {/* No width — Title absorbs whatever the fixed columns leave over. */}
                  <th className={TH_CLASS} onClick={() => handleSort("title")}>
                    Title
                    {sortBy === "title" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[110px]`}
                    onClick={() => handleSort("status")}
                  >
                    Status
                    {sortBy === "status" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[110px]`}
                    onClick={() => handleSort("visibility")}
                  >
                    Visibility
                    {sortBy === "visibility" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[150px] hidden md:table-cell`}
                    onClick={() => handleSort("collection")}
                  >
                    Collection
                    {sortBy === "collection" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[160px] hidden lg:table-cell`}
                    onClick={() => handleSort("authorName")}
                  >
                    Author
                    {sortBy === "authorName" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[170px] hidden lg:table-cell`}
                    onClick={() => handleSort("organization")}
                  >
                    Organization
                    {sortBy === "organization" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[90px] hidden md:table-cell`}
                    onClick={() => handleSort("viewCount")}
                  >
                    Views
                    {sortBy === "viewCount" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[120px] hidden lg:table-cell`}
                    onClick={() => handleSort("createdAt")}
                  >
                    Created
                    {sortBy === "createdAt" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[120px]`}
                    onClick={() => handleSort("updatedAt")}
                  >
                    Updated
                    {sortBy === "updatedAt" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border">
                {isFetching && articles.length === 0
                  ? Array.from({ length: 5 }).map((_, index) => (
                      <tr key={`loading-${index}`} className="animate-pulse">
                        <td className={TD_CLASS}>
                          <div className="h-4 w-4 bg-muted rounded"></div>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="h-4 bg-muted rounded w-48"></div>
                          <div className="h-3 bg-muted rounded w-32 mt-1"></div>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="h-5 bg-muted rounded-full w-16"></div>
                        </td>
                        {/* Visibility — was missing here, leaving the skeleton a column short
                            of the header it stands in for. */}
                        <td className={TD_CLASS}>
                          <div className="h-5 bg-muted rounded-full w-16"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden md:table-cell`}>
                          <div className="h-4 bg-muted rounded w-20"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="h-4 bg-muted rounded w-24"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="h-4 bg-muted rounded w-28"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden md:table-cell`}>
                          <div className="h-4 bg-muted rounded w-12"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="h-4 bg-muted rounded w-20"></div>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="h-4 bg-muted rounded w-20"></div>
                        </td>
                      </tr>
                    ))
                  : articles.map((article) => (
                      <tr
                        key={article.id}
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => {
                          navigateTo.article(article.id);
                        }}
                      >
                        {/* Ticking a box isn't opening the article */}
                        <td className={TD_CLASS} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.has(article.id)}
                            onCheckedChange={(checked) => toggleSelected(article, checked === true)}
                            aria-label={`Select "${article.title}"`}
                          />
                        </td>
                        <td className={TD_CLASS}>
                          <div className="flex items-center gap-2">
                            {/* A real link: reachable by keyboard, and it can open in a new tab. The row's own click
                                handler covers the rest of the row for mouse users. */}
                            <Link
                              to={getPath.article(article.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="text-foreground font-medium text-xs sm:text-sm block truncate hover:underline focus-visible:underline"
                              title={article.title}
                            >
                              {article.title}
                            </Link>
                            {article.isFeatured && (
                              <Star className="h-4 w-4 text-yellow-500 fill-current flex-shrink-0" />
                            )}
                          </div>
                          {article.summary && (
                            <div
                              className="text-xs text-muted-foreground truncate mt-1"
                              title={article.summary}
                            >
                              {article.summary}
                            </div>
                          )}
                        </td>
                        <td className={TD_CLASS}>
                          <Badge
                            className={getStatusColor(article.status)}
                            title={
                              article.scheduledPublishAt
                                ? `Scheduled to publish ${formatDateTime(article.scheduledPublishAt)}`
                                : undefined
                            }
                          >
                            {getStatusLabel(article.status)}
                          </Badge>
                          {article.status === ArticleStatus.Published && article.hasDraft && (
                            <div className="text-[11px] text-muted-foreground mt-0.5">Unpublished changes</div>
                          )}
                          {article.status === ArticleStatus.Draft && article.scheduledPublishAt && (
                            <div className="text-[11px] text-muted-foreground mt-0.5">Scheduled</div>
                          )}
                        </td>
                        <td className={TD_CLASS}>
                          <Badge className={ArticleVisibilityColors[article.visibility as ArticleVisibility]}>
                            {ArticleVisibilityLabels[article.visibility as ArticleVisibility] || 'Public'}
                          </Badge>
                        </td>
                        <td className={`${TD_CLASS} hidden md:table-cell`}>
                          <span
                            className="text-sm text-foreground"
                            title={article.collectionPath || article.collectionName || undefined}
                          >
                            {article.collectionPath || article.collectionName || "—"}
                          </span>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span
                              className="text-sm text-foreground truncate"
                              title={article.authorName}
                            >
                              {article.authorName}
                            </span>
                          </div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span
                              className="text-sm text-foreground truncate"
                              title={getOrganizationName(article.organizationId)}
                            >
                              {getOrganizationName(article.organizationId)}
                            </span>
                          </div>
                        </td>
                        <td className={`${TD_CLASS} hidden md:table-cell`}>
                          <div className="flex items-center gap-1">
                            <Eye className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm text-foreground">
                              {article.viewCount.toLocaleString()}
                            </span>
                          </div>
                        </td>
                        {/* Date-only keeps two date columns affordable; full timestamp on hover. */}
                        <td
                          className={`${TD_CLASS} text-xs text-muted-foreground hidden lg:table-cell`}
                          title={formatDateTime(article.createdAt)}
                        >
                          {formatDateOnly(article.createdAt)}
                        </td>
                        <td
                          className={`${TD_CLASS} text-xs text-muted-foreground`}
                          title={formatDateTime(article.updatedAt)}
                        >
                          {formatDateOnly(article.updatedAt)}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          {/* Sits outside the scrollport so it stays pinned to the card edge rather than
              travelling with the content. `foreground` inverts with the theme. */}
          {scrollEdges.left && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-foreground/20 to-transparent"
            />
          )}
          {scrollEdges.right && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-foreground/20 to-transparent"
            />
          )}
        </div>

        {/* Pagination and Record Count */}
        <div className="bg-background px-6 py-3 border-t border-border">
          {totalPages > 1 ? (
            <div className="flex items-center justify-between">
              {/* Mobile stacked layout */}
              <div className="flex flex-col gap-3 sm:hidden w-full">
                {/* Page size and record count - centered */}
                <div className="flex flex-col gap-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <label className="text-xs text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-xs text-foreground">per page</span>
                  </div>
                  <p className="text-xs text-foreground">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
                    <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>
                {/* Pagination buttons - full width */}
                <div className="flex justify-between gap-2">
                  <Button
                    variant="outline"
                    onClick={() => updateFilters({ page: Math.max(1, currentPage - 1) })}
                    disabled={currentPage === 1 || isLoading}
                    className="flex-1"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateFilters({ page: Math.min(totalPages, currentPage + 1) })}
                    disabled={currentPage === totalPages || isLoading}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              </div>

              {/* Desktop layout */}
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-foreground">per page</span>
                  </div>
                  <div>
                    <p className="text-sm text-foreground">
                      Showing{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * pageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, totalCount)}
                      </span>{" "}
                      of <span className="font-medium">{totalCount}</span> results
                    </p>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <Button
                      variant="outline"
                      onClick={() => updateFilters({ page: Math.max(1, currentPage - 1) })}
                      disabled={currentPage === 1 || isLoading}
                      className="rounded-l-md"
                    >
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    {(() => {
                      const pages: number[] = [];
                      const showPages = 5; // Number of page buttons to show

                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      const endPage = Math.min(totalPages, startPage + showPages - 1);

                      // Adjust start if we're near the end
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }

                      return pages.map(page => (
                        <Button
                          key={page}
                          variant={page === currentPage ? "default" : "outline"}
                          onClick={() => updateFilters({ page })}
                          disabled={isLoading}
                          className="min-w-[40px]"
                        >
                          {page}
                        </Button>
                      ));
                    })()}

                    <Button
                      variant="outline"
                      onClick={() => updateFilters({ page: Math.min(totalPages, currentPage + 1) })}
                      disabled={currentPage === totalPages || isLoading}
                      className="rounded-r-md"
                    >
                      Next
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          ) : (
            // Show record count and page size selector even when there's only one page
            <div className="flex items-center justify-between">
              {/* Mobile stacked layout */}
              <div className="flex flex-col gap-2 sm:hidden w-full text-center">
                <div className="flex items-center justify-center gap-2">
                  <label className="text-xs text-foreground">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs text-foreground">per page</span>
                </div>
                <p className="text-xs text-foreground">
                  Showing{" "}
                  <span className="font-medium">
                    {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>

              {/* Desktop layout */}
              <div className="hidden sm:flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-foreground">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-foreground">per page</span>
                </div>
                <div>
                  <p className="text-sm text-foreground">
                    Showing{" "}
                    <span className="font-medium">
                      {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalCount)}
                    </span>{" "}
                    of <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {articles.length === 0 && !isLoading && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="h-12 w-12 text-muted-foreground" />
            </div>

            {/* Every organization selected is the default, not a filter - otherwise a new organization would never
                see the welcome state */}
            {searchQuery ||
            statusFilters.length > 0 ||
            visibilityFilters.length > 0 ||
            collectionFilters.length > 0 ||
            (organizationFilters.length > 0 && organizationFilters.length < (availableOrganizations?.length ?? 0)) ||
            featuredFilter !== "" ||
            fromDate ||
            toDate ? (
              // Filtered empty state
              <>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  No articles match your search
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  We couldn't find any articles matching your current filters.
                  Try adjusting your search criteria or create a new article.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                  <Link to={getPath.newArticle()}>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Article
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              // No articles at all
              <>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  Welcome to your Knowledge Base
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  You haven't created any articles yet. Start building your
                  knowledge base by creating your first article.
                </p>
                <Link to={getPath.newArticle()}>
                  <Button size="lg">
                    <Plus className="mr-2 h-5 w-5" />
                    Create Your First Article
                  </Button>
                </Link>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
